import { Link } from "react-router-dom";
import { ArrowRight, Star, Phone, BookOpen } from "lucide-react";
import { Button } from "@/components/ui/button";

const CHRIS_PHOTO = "https://customer-assets.emergentagent.com/job_d3e173b2-dc8f-433a-a6c6-4622316d8ee5/artifacts/5dptin5a_brookebarclayphotography1-154.jpg";

export const HeroSection = () => {
  return (
    <section 
      className="min-h-[85vh] flex items-center bg-[#f0efeb] pt-20 md:pt-24"
      data-testid="hero-section"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12 py-8 md:py-12 w-full">
        <div className="grid lg:grid-cols-2 gap-8 lg:gap-16 items-center">
          
          {/* Left Column - Photo */}
          <div className="order-2 lg:order-1 flex justify-center lg:justify-start">
            <div className="relative">
              <img 
                src={CHRIS_PHOTO} 
                alt="Chris Pierog - Grays Harbor Realtor" 
                className="w-full max-w-md lg:max-w-lg object-cover shadow-2xl"
                data-testid="hero-photo"
              />
              {/* Decorative element */}
              <div className="absolute -bottom-4 -right-4 w-full h-full bg-[#132723]/10 -z-10" />
            </div>
          </div>

          {/* Right Column - Content */}
          <div className="order-1 lg:order-2 text-center lg:text-left">
            {/* Tagline */}
            <p className="text-sm uppercase tracking-widest text-[#4d6153] font-medium mb-4">
              Grays Harbor & Pacific County
            </p>
            
            {/* Main Headline */}
            <h1 
              className="text-4xl sm:text-5xl lg:text-6xl font-serif tracking-tight leading-tight text-[#132723] mb-6"
              data-testid="hero-heading"
            >
              Your Trusted<br />
              <span className="text-[#00453d]">Local Realtor</span>
            </h1>
            
            {/* Subheadline */}
            <p 
              className="text-lg md:text-xl text-[#4d6153] mb-8 max-w-xl mx-auto lg:mx-0"
              data-testid="hero-subheading"
            >
              Helping families buy and sell homes across Grays Harbor and Pacific County with expert local knowledge and proven results.
            </p>

            {/* CTA Buttons */}
            <div className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start mb-6">
              <Button
                asChild
                className="bg-[#132723] text-white hover:bg-[#00453d] px-8 py-6 text-lg font-medium"
                data-testid="hero-cta-button"
              >
                <a 
                  href="https://homevalue.graysharborrealtor.com/" 
                  target="_blank" 
                  rel="noopener noreferrer"
                >
                  Get Your Free Home Value
                  <ArrowRight className="w-5 h-5 ml-2" />
                </a>
              </Button>
              
              <a 
                href="tel:+13605810167"
                className="inline-flex items-center justify-center gap-2 px-6 py-3 border-2 border-[#132723] text-[#132723] hover:bg-[#132723] hover:text-white transition-colors font-medium"
                data-testid="hero-phone-button"
              >
                <Phone className="w-4 h-4" />
                (360) 581-0167
              </a>
            </div>

            {/* Secondary Link - Buyer Guide */}
            <div className="flex justify-center lg:justify-start mb-8">
              <Link 
                to="/buyer-guide"
                className="inline-flex items-center gap-2 text-[#00453d] hover:text-[#132723] transition-colors font-medium"
                data-testid="hero-buyer-guide-link"
              >
                <BookOpen className="w-4 h-4" />
                Looking to buy? Read the Complete Buyer&apos;s Guide
                <ArrowRight className="w-4 h-4" />
              </Link>
            </div>

            {/* Trust Indicators */}
            <div className="flex flex-col sm:flex-row items-center justify-center lg:justify-start gap-6 pt-6 border-t border-[#e2e5e2]">
              <div className="flex items-center gap-2">
                <div className="flex gap-0.5">
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} className="w-4 h-4 fill-yellow-400 text-yellow-400" />
                  ))}
                </div>
                <span className="text-sm text-[#4d6153]">5.0 on Zillow</span>
              </div>
              
              <div className="hidden sm:block w-px h-6 bg-[#e2e5e2]" />
              
              <p className="text-sm text-[#4d6153]">
                <span className="font-semibold text-[#132723]">$50M+</span> in Sales
              </p>
              
              <div className="hidden sm:block w-px h-6 bg-[#e2e5e2]" />
              
              <p className="text-sm text-[#4d6153]">
                <span className="font-semibold text-[#132723]">100+</span> Happy Clients
              </p>
            </div>

            {/* Agent Name Card */}
            <div className="mt-8 inline-block bg-white p-4 shadow-sm border border-[#e2e5e2]">
              <p className="text-lg font-serif text-[#132723]">Chris Pierog</p>
              <p className="text-sm text-[#4d6153]">Licensed Realtor® | Spivey Realty Group</p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default HeroSection;
